/**
 * Общая физика жестов приложения.
 *
 * Числа не новые: они приехали из свайпа анкет (Profile/ProfileCard.tsx) в
 * карусель «Как пользоваться» (overlays/UserGuideOverlay.tsx), и на третьем
 * потребителе — шторке QR-кода — копипаст пришлось бы делать в третий раз.
 * docs/DESIGN_SYSTEM.md §6.4 отдельным пунктом просит свести «семь пружин» к
 * двум, поэтому вместо третьей копии — один модуль.
 *
 * Лента анкет — эталон по словам продакт-оунера («лучшее место в приложении»),
 * поэтому её значения здесь канон, а не усреднение.
 *
 * Источник физики — apple-design, «Designing Fluid Interfaces» (WWDC 2018):
 * §5 передача скорости, §6 проекция точки остановки, §9 резинка на границах,
 * §10 гистерезис направления.
 */

/** Гистерезис: до этого смещения направление жеста не зафиксировано (§10). */
export const DIRECTION_LOCK_PX = 10;

/** Скорость броска (px/с), после которой жест довёрстывается независимо от
 *  пройденного пути. Тот же порог, что у свайпа анкет — ProfileCard.SWIPE_VELOCITY. */
export const FLING_VELOCITY = 520;

/** Затухание для проекции точки остановки броска (Apple, §6). */
export const DECELERATION_RATE = 0.998;

/** Резинка на краях: чем дальше за край, тем меньше поверхность следует за пальцем. */
export const RUBBERBAND_CONSTANT = 0.55;

/** Окно усреднения скорости отпускания, мс. */
export const VELOCITY_WINDOW_MS = 90;

/** Кривая проекта — `--rla-ease-out` из docs/DESIGN_SYSTEM.md §3.5. */
export const EASE_OUT = [0.23, 1, 0.32, 1] as const;

/** Короткое проявление вместо движения при «уменьшить движение» (§14, docs §6.8). */
export const CROSSFADE_DURATION = 0.18;

export type GestureSpring = {type: "spring"; stiffness: number; damping: number};

/** Возврат на месте: жест не добрал до порога.
 *  ζ≈0.83, отклик ≈0.3 с — «ящик/шторка» по таблице apple-design §4. */
export const SETTLE_SPRING: GestureSpring = {type: "spring", stiffness: 420, damping: 34};

/** Довёрстывание до соседнего состояния: вылет карточки из ленты.
 *  ζ≈1.06, отклик ≈0.4 с — «перемещение» по таблице apple-design §4, без перелёта. */
export const PAGE_SPRING: GestureSpring = {type: "spring", stiffness: 200, damping: 30};

export const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

/** Куда доедет бросок с такой скоростью (px). Экспоненциальное затухание, как у скролла.
 *  Именно так считает Apple — не школьное v²/(2a), см. apple-design §6. */
export function projectEndpoint(velocity: number) {
    return (velocity / 1000) * DECELERATION_RATE / (1 - DECELERATION_RATE);
}

/** Прогрессивное сопротивление за краем: не стена, а резинка (§9). */
export function rubberband(overshoot: number, dimension: number) {
    return (overshoot * dimension * RUBBERBAND_CONSTANT) / (dimension + RUBBERBAND_CONSTANT * Math.abs(overshoot));
}

/** Одна точка траектории пальца: координата по рабочей оси и её момент времени. */
export type GestureSample = {t: number; v: number};

/**
 * Скорость отпускания (px/с) по хвосту траектории.
 *
 * Берём не последние два события, а окно VELOCITY_WINDOW_MS: два подряд идущих
 * pointermove могут прийти с разницей в доли миллисекунды и дать выброс на
 * порядок — жест «дёрнулся» бы там, где палец вёлся ровно.
 */
export function velocityFromSamples(samples: readonly GestureSample[]): number {
    if (samples.length < 2) return 0;
    const last = samples[samples.length - 1];
    const first = samples.find((sample) => last.t - sample.t <= VELOCITY_WINDOW_MS) ?? samples[0];
    const elapsed = last.t - first.t;
    return elapsed > 0 ? ((last.v - first.v) / elapsed) * 1000 : 0;
}

/** Хвост траектории фиксированной длины — большего для скорости не нужно. */
export function pushSample(samples: GestureSample[], sample: GestureSample) {
    samples.push(sample);
    if (samples.length > 8) samples.shift();
}
